/**
 * Shared long-weekday date formatter (UID-410 code-review) — extracted out of
 * `ship-as-ready-breakdown.ts` and `custom-region-tiers.ts`, which had
 * declared byte-identical `WEEKDAYS`/`MONTHS` arrays and near-identical
 * formatters. Both prototype call sites use the same `weekday:'long'` style
 * (`_fmtDow`, `Fictiv Platform.dc.html` line ~7290) — only the day-padding
 * differs between callers (region-tier zero-pads; ship-as-ready doesn't).
 */

/** Full weekday names, Sunday…Saturday. */
export const LONG_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

/** Short month names, Jan…Dec. */
export const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export interface FormatLongWeekdayDateOptions {
  /** Zero-pad the day to 2 digits (e.g. "Aug 05" instead of "Aug 5"). */
  readonly padDay?: boolean
}

/**
 * e.g. `formatLongWeekdayDate(d)` -> "Wednesday, Aug 19"
 * `formatLongWeekdayDate(d, { padDay: true })` -> "Aug 05" (zero-padded day).
 * Reads the date via `getUTC*` — callers are responsible for constructing
 * `date` in whatever timezone semantics they need.
 */
export function formatLongWeekdayDate(date: Date, opts?: FormatLongWeekdayDateOptions): string {
  const day = opts?.padDay ? String(date.getUTCDate()).padStart(2, '0') : date.getUTCDate()
  return `${LONG_WEEKDAYS[date.getUTCDay()]}, ${SHORT_MONTHS[date.getUTCMonth()]} ${day}`
}
